import { isEmpty } from '@intelligence/v3/utils/common';
import { getFiniteNumberValue, getStringArrayApiValueByItem } from './shared/valueUtils';
import { getValidRevenueGrowthFilterValue } from './revenueGrowth/utils';
import { FILTER_TYPE } from './filterTypes';
import {
  APPMAGIC_TAG_KEYS,
  APPMAGIC_TAG_MAP,
  SENSOR_TOWER_TAG_KEYS,
  SENSOR_TOWER_TAG_MAP,
  SENSOR_TOWER_TAG_THEME,
} from '@intelligence/components/Filter/FilterBase/_config';

import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';

const TAG_VALUE_JOINER = '@';
const METRIC_FILTER_FIELD_ORDER = ['revenueGrowth', 'revenueRange', 'arpuRange', 'activeUsersRange', 'downloadsRange'];

export const METRIC_RANGE_FILTER_CONFIG: IRecord<{
  source: string;
  metric: string;
}> = {
  revenueRange: {
    source: 'revenueRange',
    metric: 'revenue',
  },
  arpuRange: {
    source: 'arpuRange',
    metric: 'arpu',
  },
  activeUsersRange: {
    source: 'activeUsersRange',
    metric: 'au',
  },
  downloadsRange: {
    source: 'downloadsRange',
    metric: 'download',
  },
};

export const isSensorTowerTagField = (field: string): boolean => {
  return SENSOR_TOWER_TAG_KEYS.includes(field);
};

export const isAppMagicTagField = (field: string): boolean => {
  return APPMAGIC_TAG_KEYS.includes(field);
};

const getMetricRangeApiParams = (field: string, value: unknown): IRecord<any[]> | null => {
  const config = METRIC_RANGE_FILTER_CONFIG[field];

  if (!config || isEmpty(value)) {
    return null;
  }

  const rangeValue = value as INumberRangeValue | undefined;
  const minValue = getFiniteNumberValue(rangeValue?.min);
  const maxValue = getFiniteNumberValue(rangeValue?.max);
  const metricFilters: any[] = [];

  if (minValue !== null) {
    metricFilters.push({
      metric: config.metric,
      opt: '>=',
      value: minValue,
    });
  }

  if (maxValue !== null) {
    metricFilters.push({
      metric: config.metric,
      opt: '<',
      value: maxValue,
    });
  }

  if (isEmpty(metricFilters)) {
    return null;
  }

  return {
    [config.source]: metricFilters,
  };
};

const getRevenueGrowthApiParams = (value: unknown): IRecord<any[]> | null => {
  const normalizedValue = getValidRevenueGrowthFilterValue(value as IComparableValue);

  if (!normalizedValue) {
    return null;
  }

  return {
    revenueGrowth: [
      {
        metric: 'revenue_ratio',
        opt: normalizedValue.opt,
        value: normalizedValue.value,
        ratio_type: 1,
      },
    ],
  };
};

export const mergeMetricFilter = (target: IRecord<any[]>, source?: IRecord<any[]> | null): void => {
  if (isEmpty(source)) {
    return;
  }

  Object.keys(source).forEach((key) => {
    target[key] = (target[key] || []).concat(source[key] || []);
  });
};

export const collectMetricFilterData = (params: {
  target: IRecord<any[]>;
  field: string;
  type: string;
  value: unknown;
}): boolean => {
  const { target, field, type, value } = params;

  if (METRIC_RANGE_FILTER_CONFIG[field]) {
    mergeMetricFilter(target, getMetricRangeApiParams(field, value));
    return true;
  }

  if (type === FILTER_TYPE.REVENUE_GROWTH) {
    mergeMetricFilter(target, getRevenueGrowthApiParams(value));
    return true;
  }

  return false;
};

export const buildMetricFilterByDataSource = (metricFilter: IRecord<any[]>, dataSources?: string[]): IRecord<any[]> | null => {
  if (isEmpty(metricFilter) || isEmpty(dataSources)) {
    return null;
  }

  const filters = METRIC_FILTER_FIELD_ORDER.reduce<any[]>((list, field) => {
    return list.concat(metricFilter[field] || []);
  }, []);

  return dataSources.reduce<IRecord<any[]>>((result, dataSource) => {
    result[dataSource] = filters.map((filterItem) => {
      return {
        ...filterItem,
      };
    });

    return result;
  }, {});
};

const getTagFilterValue = (item: IFilterItemConfig, value: unknown): string[] | null => {
  const apiValue = getStringArrayApiValueByItem(item, value);

  if (isEmpty(apiValue.value) && !apiValue.isAllSelected) {
    return null;
  }

  return apiValue.value;
};

export const collectTagFilterData = (
  target: IRecord<{ value: string[] }>,
  item: IFilterItemConfig,
  filterValue: unknown,
): void => {
  if (isEmpty(filterValue)) {
    return;
  }

  const tagValue = getTagFilterValue(item, filterValue);

  if (tagValue === null) {
    return;
  }

  target[item.field] = {
    value: tagValue,
  };
};

const parseSensorTowerThemeValue = (
  value: string,
): {
  tagName: string;
  childTagName: string;
} | null => {
  const parts = value.split(TAG_VALUE_JOINER);

  if (parts.length !== 2) {
    return null;
  }

  const [tagName, childTagName] = parts;

  if (isEmpty(tagName) || isEmpty(childTagName)) {
    return null;
  }

  return {
    tagName,
    childTagName,
  };
};

const buildSensorTowerThemeTags = (values: string[]): any[] => {
  return values.reduce<any[]>((result, value) => {
    const parsedValue = parseSensorTowerThemeValue(value);

    if (!parsedValue) {
      return result;
    }

    const { tagName, childTagName } = parsedValue;
    const index = result.findIndex((item) => item?.name === tagName);

    if (index > -1) {
      result[index].children.push({
        name: childTagName,
        children: [],
      });
    } else {
      result.push({
        name: tagName,
        children: [
          {
            name: childTagName,
            children: [],
          },
        ],
      });
    }

    return result;
  }, []);
};

export const buildSensorTowerValueFilter = (filterData: IRecord<{ value: string[] }>): IRecord<any[]> => {
  const sensorTowerTags = SENSOR_TOWER_TAG_KEYS.reduce<any[]>((result, field) => {
    const values = filterData[field]?.value || [];

    if (isEmpty(values)) {
      return result;
    }

    if (field === SENSOR_TOWER_TAG_THEME) {
      const themeTags = buildSensorTowerThemeTags(values);

      if (isEmpty(themeTags)) {
        return result;
      }

      result.push({
        name: SENSOR_TOWER_TAG_MAP[field] || '',
        children: themeTags,
      });

      return result;
    }

    result.push({
      name: SENSOR_TOWER_TAG_MAP[field] || '',
      children: values.map((item) => {
        return {
          name: item,
          children: [],
        };
      }),
    });

    return result;
  }, []);

  if (isEmpty(sensorTowerTags)) {
    return {};
  }

  return {
    tags: sensorTowerTags,
  };
};

export const buildAppMagicValueFilter = (filterData: IRecord<{ value: string[] }>): IRecord<IRecord<string[]>> => {
  const appMagicTags = APPMAGIC_TAG_KEYS.reduce<IRecord<string[]>>((result, field) => {
    const apiFieldName = APPMAGIC_TAG_MAP[field];

    if (!isEmpty(apiFieldName)) {
      result[apiFieldName] = filterData[field]?.value || [];
    }

    return result;
  }, {});

  return {
    app_magic_tags: appMagicTags,
  };
};
